import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import ExcelJS from "exceljs";
import Export from "../../../../components/Export";
import printChart from "../../../../PrintChart";
import DateRangeReport from "../../../../components/DateRangeReport";
import DefaultState from "../../../sales-and-transactions/components/DefaultState";
import getCustomerCategoriesCount from "./CustomerCategoriesCount";
import InitialPrompt from "../../../../components/InitialPrompt";
import CustomSelect from "../../../../components/CustomSelect";
import PageHeader from "../../../../components/PageHeader";

function CustomerCountPage({
  transactions,
  onChangeDate,
  onChangeStore,
  dateField,
  store,
  storess,
  range,
  notFound,
}) {
  const [selectedStore, setSelectedStore] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getUniqueStores = (transactions) => {
    const storeSet = new Set();
    transactions.forEach((transaction) => {
      const storeCode = transaction.store_code.replace("ANGELSPIZZA_", "");
      storeSet.add(storeCode);
    });
    return Array.from(storeSet);
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const tranDate = new Date(transaction.tran_date);
      const start = startDate ? new Date(startDate) : new Date(0);
      let end = endDate ? new Date(endDate) : new Date();
      end.setHours(23, 59, 59, 999);

      return tranDate >= start && tranDate <= end;
    });
  }, [transactions, startDate, endDate]);

  const uniqueStores = useMemo(
    () => getUniqueStores(filteredTransactions),
    [filteredTransactions],
  );

  const customerCategories = useMemo(
    () => getCustomerCategoriesCount(filteredTransactions, selectedStore),
    [filteredTransactions, selectedStore],
  );

  const loyalistCount = customerCategories.loyalists.length;
  const potentialLoyalistCount = customerCategories.potentialLoyalists.length;
  const promisingCount = customerCategories.promising.length;
  const hesitantCount = customerCategories.hesitant.length;
  const needAttentionCount = customerCategories.needAttention.length;
  const detractorsCount = customerCategories.detractors.length;
  const customerCount = customerCategories.customerCount;

  const categories = [
    "Loyalist",
    "Potential Loyalist",
    "Promising",
    "Hesitant",
    "Need Attention",
    "Detractors",
  ];

  const colors = [
    "#16a34a",
    "#0891b2",
    "#6366f1",
    "#9333ea",
    "#ea580c",
    "#e11d48",
  ];

  const seriesData = [
    loyalistCount,
    potentialLoyalistCount,
    promisingCount,
    hesitantCount,
    needAttentionCount,
    detractorsCount,
  ];

  const chartOptions = {
    chart: {
      type: "column",
      style: { fontFamily: "inherit" },
    },
    title: {
      text: "Customer Count per Customer Type",
      style: { fontSize: "16px", fontWeight: "bold", color: "#333" },
    },
    xAxis: {
      categories: categories,
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: { text: "Count" },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      symbolRadius: 4,
      itemStyle: { fontSize: "12px", fontWeight: "500", color: "#555" },
    },
    tooltip: {
      headerFormat:
        '<span style="font-size:14px;font-weight:bold">{point.key}</span><br/>',
      pointFormat:
        '<span style="color:{point.color}">\u25CF</span> Count: <b>{point.y}</b>',
    },
    plotOptions: {
      column: {
        borderRadius: 5,
        dataLabels: {
          enabled: true,
          format: "{y}",
          style: { fontWeight: "bold", color: "#333", textOutline: "none" },
        },
      },
    },
    series: categories.map((name, i) => ({
      name: name,
      data: categories.map((_, j) => (j === i ? seriesData[i] : null)),
      color: colors[i],
      showInLegend: true,
    })),
    credits: { enabled: false },
  };

  const data = {
    labels: categories,
    datasets: [{ data: seriesData }],
  };

  const getDateRange = () => {
    const startRaw = range?.tran_date?.["$gte"] || "";
    const endRaw = range?.tran_date?.["$lte"] || "";
    const start = startRaw ? startRaw.split(" ")[0].split("T")[0] : "N/A";
    const end = endRaw ? endRaw.split(" ")[0].split("T")[0] : "N/A";
    return { start, end };
  };

  const exportCsv = async () => {
    const { start, end } = getDateRange();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Customer Count");

    // -- Title row --
    const titleRow = worksheet.getRow(1);
    titleRow.getCell(1).value = "Customer Count per Customer Type";
    titleRow.getCell(1).font = {
      bold: true,
      size: 14,
      color: { argb: "FF000000" },
    };
    titleRow.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF0F0F0" },
    };
    titleRow.height = 28;
    worksheet.mergeCells(1, 1, 1, 4);

    // -- Info rows --
    const infoStyle = { font: { size: 11, color: { argb: "FF555555" } } };
    const infoRow1 = worksheet.getRow(2);
    infoRow1.getCell(1).value = `Store: ${storess || "All"}`;
    Object.assign(infoRow1.getCell(1), infoStyle);
    const infoRow2 = worksheet.getRow(3);
    infoRow2.getCell(1).value = `Date Range: ${start} to ${end}`;
    Object.assign(infoRow2.getCell(1), infoStyle);

    // -- Header row --
    const headers = ["Category", "Count"];
    const headerRow = worksheet.getRow(5);
    headers.forEach((header, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = header;
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF333333" },
      };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    headerRow.height = 22;

    // -- Data rows with segment colors --
    const colorArgbs = [
      "FF16A34A",
      "FF0891B2",
      "FF6366F1",
      "FF9333EA",
      "FFEA580C",
      "FFE11D48",
    ];

    const rows = categories.map((name, i) => ({
      category: name,
      count: seriesData[i],
      colorArgb: colorArgbs[i],
    }));

    let totalCount = 0;
    rows.forEach((row, i) => {
      const excelRow = worksheet.getRow(6 + i);
      totalCount += row.count;

      // Category cell with colored left accent
      const catCell = excelRow.getCell(1);
      catCell.value = row.category;
      catCell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      catCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: row.colorArgb },
      };
      catCell.alignment = { vertical: "middle", horizontal: "left" };

      // Count cell
      const countCell = excelRow.getCell(2);
      countCell.value = row.count;
      countCell.numFmt = "#,##0";
      countCell.alignment = { vertical: "middle", horizontal: "center" };

      // Color indicator cell
      // const colorCell = excelRow.getCell(3);
      // colorCell.value = "";
      // colorCell.fill = {
      //   type: "pattern",
      //   pattern: "solid",
      //   fgColor: { argb: row.colorArgb },
      // };

      // Borders for all cells
      [catCell, countCell].forEach((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFD3D3D3" } },
          left: { style: "thin", color: { argb: "FFD3D3D3" } },
          bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
          right: { style: "thin", color: { argb: "FFD3D3D3" } },
        };
      });

      excelRow.height = 20;
    });

    // -- Total row --
    const totalRowNum = 6 + rows.length;
    const totalRow = worksheet.getRow(totalRowNum);
    const totalCatCell = totalRow.getCell(1);
    totalCatCell.value = "TOTAL";
    totalCatCell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    totalCatCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF70AD47" },
    };
    totalCatCell.alignment = { vertical: "middle", horizontal: "left" };

    const totalCountCell = totalRow.getCell(2);
    totalCountCell.value = totalCount;
    totalCountCell.numFmt = "#,##0";
    totalCountCell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    totalCountCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF70AD47" },
    };
    totalCountCell.alignment = { vertical: "middle", horizontal: "center" };

    // const totalColorCell = totalRow.getCell(3);
    // totalColorCell.value = "";
    // totalColorCell.fill = {
    //   type: "pattern",
    //   pattern: "solid",
    //   fgColor: { argb: "FF70AD47" },
    // };

    [totalCatCell, totalCountCell].forEach((cell) => {
      cell.border = {
        top: { style: "medium" },
        left: { style: "medium" },
        bottom: { style: "medium" },
        right: { style: "medium" },
      };
    });
    totalRow.height = 22;

    // -- Column widths --
    worksheet.columns = [{ width: 25 }, { width: 15 }, { width: 8 }];

    // -- Download --
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customer-count_${start}_${end}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const chartId = 40;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <PageHeader
          title="Customer Count"
          subtitle="Overview of customer volume and visit patterns"
          icon="bi bi-people"
        />
        <Export exportCsv={exportCsv} exportPdf={() => printChart(chartId)} />
      </div>
      <div className="row">
        <div className="col mb-3" style={{ maxWidth: "max-content" }}>
          <DateRangeReport onChange={onChangeDate} field={dateField} />
        </div>
        <div className="col-sm-4 mb-3">
          <CustomSelect
            label="Store"
            options={[
              // { value: "AllStore", label: "All Store" },
              ...store.map((store) => ({
                value: store.name,
                label: store.name.replace(/_/g, " "),
              })),
            ]}
            onChange={(selectedOption) => onChangeStore(selectedOption?.value)}
            isDisabled={!range}
            error={!range ? "Please select a date range first" : ""}
            icon="bi bi-shop"
            placeholder="Select Store"
            isClearable={true}
          />
        </div>
      </div>

      {transactions.length > 0 ? (
        <div id="40">
          <div className="card">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
          <div className="print-only-content">
            <h2>Report Details</h2>
            <div className="print-only-content">
              <div>
                <h3>Brand: Angels Pizza</h3>
                <h3>Store: {selectedStore}</h3>
                <h3>
                  Date: {startDate} - {endDate}
                </h3>
              </div>
            </div>
            <div style={{ fontSize: "20px" }}>
              <table className="table text-center">
                <thead>
                  <tr className="d-flex">
                    <th>Type</th>
                    <th className="ms-5">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.labels.map((label, index) => (
                    <tr className="d-flex" key={label}>
                      <td>{label}</td>
                      <td>{data.datasets[0].data[index]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : !storess || !range ? (
        <InitialPrompt twoFilter={true} />
      ) : (
        // :
        // !notFound ? (
        //   <div className="text-center mt-5">
        //     <p className="text-muted fs-5">
        //       📭 Data not found for selected filter.
        //     </p>
        //   </div>
        // )
        <div className="text-center mt-5">
          <DefaultState />
        </div>
      )}
    </>
  );
}

export default CustomerCountPage;
